import React, { useContext, createContext } from "react";
import axios from "axios";
import queryString from 'query-string';
import _ from 'lodash';
import { useGlobal } from "reactn";

import { TIMEUNIT_ID, TIMEUNIT_ID_TIMES, TIMEUNIT_ID_TIMES_MINUTES, BOOKING_STATUS_TYPE } from 'stores/const';
import useLocalStorage from "stores/useLocalStorage.js";
import { isString, genGuid, roundMinutes } from 'stores/utils';

const apiContext = createContext();

export function ProvideApi({ children }) {
  const api = useProvideApi();
  return <apiContext.Provider value={api}>{children}</apiContext.Provider>;
}

export const useApi = () => {
  return useContext(apiContext);
};

// const toISO = (date) => {
//   let dtf = new Intl.DateTimeFormat("de-CH", {
//     dateStyle: "medium",
//     timeStyle: "medium"
//   });
//   return dtf.format(date).replace(/\, /gi, 'T');
// }

// const toISODate = (strDate) => {
//   let input = strDate.substring(0, 10).split('.');
//   return input[2] + '-' + input[1] + '-' + input[0];
// }

function useProvideApi() {
  const [LSOrderId, setLSOrderId] = useLocalStorage("ls_oid", ''); //oid=order id
  const [LSObjectDetailsMap, setLSObjectDetailsMap] = useLocalStorage('ls_odm'); //odm=object details map
  const [LSGuestData, setLSGuestData] = useLocalStorage('ls_gd'); //gd=guest data

  const [modalProps, setModalProps] = useGlobal('modalProps');
  const [masterData, setMasterData] = useGlobal('masterData');
  const [searchParams, setSearchParams] = useGlobal('searchParams');
  const [defaultParams, setDefaultParams] = useGlobal('defaultParams');
  const [searchPageVars, setSearchPageVars] = useGlobal('searchPageVars');
  const [objectDetails, setObjectDetails] = useGlobal('objectDetails');
  const [currentObjId, setCurrentObjId] = useGlobal('currentObjId');
  const [currentTxnId, setCurrentTxnId] = useGlobal('currentTxnId');
  const [currentTxn, setCurrentTxn] = useGlobal('currentTxn');
  const [orderData, setOrderData] = useGlobal('orderData');
  const [cartCount, setCartCount] = useGlobal('cartCount');
  const [orderId, setOrderId] = useGlobal('orderId');
  const [isDBA, setIsDBA] = useGlobal('isDBA');
  const [offerDates, setOfferDates] = useGlobal('offerDates');
  const [orderRentDates, setOrderRentDates] = useGlobal('orderRentDates');
  const [isSameTimePeriod] = useGlobal('isSameTimePeriod');
  const [isAgbAccepted, setIsAgbAccepted] = useGlobal('isAgbAccepted');

  const apiBasePublic = '/api/public/';
  const apiBase = '/api/';

  const qString = queryString.parse(document.location.search);

  let lessorId = undefined;
  if (qString && qString.lessorId) {
    lessorId = qString.lessorId;
  }

  const orgId = lessorId || window.ict.wyby.organizationId;

  //https://medium.com/quick-code/maintainable-options-parameters-for-functions-in-javascript-9f7d5d207a65
  const dateToISOString = (date, { ignoreTime, addDays } = {}) => {
    let showTime = ignoreTime ? false : masterData.organization.showTime;
    const timezoneOffsetDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
    timezoneOffsetDate.setSeconds(0);
    if (!showTime && addDays) {
      timezoneOffsetDate.setDate(timezoneOffsetDate.getDate() + addDays)
    }
    let isoString = timezoneOffsetDate.toISOString();
    return showTime ? isoString.substring(0, 19) : isoString.substring(0, 10);
  }

  // https://portal.wy.by/api/public/Salutations
  // https://portal.wy.by/api/public/Countries
  // https://portal.wy.by/api/public/TimeZones
  // https://portal.wy.by/api/public/AddressTypes
  // https://portal.wy.by/api/public/Languages
  // https://portal.wy.by/api/public/Organization/1601a7c1-8568-40cc-bfad-d7e972e498c9/json
  // https://portal.wy.by/api/public/Organization/1601a7c1-8568-40cc-bfad-d7e972e498c9/getMinPickupTime
  // https://portal.wy.by/api/public/Organization/1601a7c1-8568-40cc-bfad-d7e972e498c9/getSectionsWithRentalObjects

  const fetchMasterData = async () => {
    //   var ict = {
    //     wyby: {
    //         organizationId: "3bf3c448-3cb6-492d-9a20-650cfcbcc9e4",
    //         orgLogo: "/clients/standard/release/assets/portals/cpc/logo.png",
    //         orgClaimImage: "/clients/standard/release/assets/portals/cpc/claimImage.jpg"
    //     }
    // };

    //const orgId = "3bf3c448-3cb6-492d-9a20-650cfcbcc9e4";
    var searchParamsTemp = {};

    axios.all([
      axios.get(`${apiBasePublic}Salutations`),
      axios.get(`${apiBasePublic}Countries`),
      axios.get(`${apiBasePublic}TimeZones`),
      axios.get(`${apiBasePublic}AddressTypes`),
      axios.get(`${apiBasePublic}Languages`),
      axios.get(`${apiBasePublic}Organization/${orgId}/json`),
      axios.get(`${apiBasePublic}Organization/${orgId}/getMinPickupTime`),
      axios.get(`${apiBasePublic}Organization/${orgId}/getSectionsWithRentalObjects?coverAlsoAccessoryCategories=1`)
    ])
      .then(
        axios.spread(async (...responses) => {
          const rspSalutations = responses[0].data;
          const rspCountries = responses[1].data;
          const rspTimeZones = responses[2].data;
          const rspAddressTypes = responses[3].data;
          const rspLanguages = responses[4].data;
          const rspOrganization = responses[5].data;
          const rspOrgMinPickupTime = responses[6].data;
          const rspOrgCategories = responses[7].data.ROOT.RentalCategory;

          let salutations = rspSalutations.ROOT.Salutation;
          salutations.map(item => {
            item.id = item.Id;
            item.text = item.Name;
            delete item.Id;
            delete item.Name;
          });

          let countries = rspCountries.ROOT.Country;
          countries.map(item => {
            item.id = item.Id;
            item.text = item.Name;
            delete item.Id;
            delete item.Name;
          });

          let timezones = rspTimeZones.ROOT.TimeZone;
          timezones.map(item => {
            item.id = item.Id;
            item.text = item.Name;
            delete item.Id;
            delete item.Name;
          });

          let languages = rspLanguages.ROOT.Language;
          languages.map(item => {
            item.id = item.Id;
            item.text = item.Name;
            delete item.Id;
            delete item.Name;
          });

          let organization = rspOrganization.ROOT.Organization[0];
          console.log('orgTimeunit:' + organization.refAllotmentTimeUnitId);
          organization.showTime = TIMEUNIT_ID_TIMES.includes(organization.refAllotmentTimeUnitId);
          if (organization.showTime) {
            organization.showMinutes = TIMEUNIT_ID_TIMES_MINUTES.includes(organization.refAllotmentTimeUnitId);
          } else {
            organization.showMinutes = undefined;
          }

          organization.settings = await getOrgSettings(organization.ReferenceCode.toLowerCase());

          let uniqueLocations = getSubcatLocations(rspOrgCategories);
          let uniqueCategories = getLocationCategories(rspOrgCategories);

          setMasterData({
            salutations,
            countries,
            organization,
            categoriesAndLocations: rspOrgCategories,
            locations: uniqueLocations,
            categories: uniqueCategories,
            timezones,
            languages
          });

          if (uniqueLocations["All"].length === 1) {
            searchParamsTemp.businessUnitId = uniqueLocations["All"][0].id;
          }

          if (uniqueCategories["All"].length === 1) {
            var firstCat = uniqueCategories["All"][0];
            searchParamsTemp.rentalCategoryId = firstCat.id;
            if (firstCat.children.length === 1) {
              searchParamsTemp.rentalSubcategoryId = firstCat.children[0].id;
            }
          }

          await setDefaultDateTimes(searchParamsTemp);
          setSearchParams({ ...searchParams, ...searchParamsTemp });
          setDefaultParams({ ...defaultParams, ...searchParamsTemp });

          var boolShowCat = (uniqueCategories["All"].length > 1 ||
            (uniqueCategories["All"].length === 1 && uniqueCategories["All"][0].children.length > 1));

          setSearchPageVars({
            ...searchPageVars,
            locations: uniqueLocations,
            categories: uniqueCategories,
            showLocation: uniqueLocations["All"].length > 1,
            showCategory: boolShowCat,
            showMaps: !organization.HideMap,
            loaded: true
          });

          if (LSOrderId) {
            getOrderDetails(LSOrderId);
          }
        })
      )
      .catch(errors => {
        // react on errors.
        console.error(errors);
      });
  }

  const getObjFeatures = async (rentalCategoryId, rentalSubcategoryId, businessUnitId) => {
    let schParams = { ...searchParams, rentalCategoryId, rentalSubcategoryId, businessUnitId };
    schParams = clean(schParams);
    schParams.organizationId = window.ict.wyby.organizationId;
    // schParams.pageNumber = 0;
    // schParams.pageSize = 0;
    schParams.startDate = dateToISOString(schParams.startDate);
    schParams.endDate = dateToISOString(schParams.endDate, { addDays: 1 });
    let queryString = Object.keys(schParams).map(key => schParams[key] ? key + '=' + schParams[key] : '').join('&');
    queryString += "&pageNumber=0&pageSize=0";

    const response = await axios.get(`${apiBasePublic}SearchObjectsWithFeatures?${queryString}`);
    const rspObjFeatures = response.data.ROOT.RentalParameterFacet;
    return getObjFeaturesFromRPFacets(rspObjFeatures);
  }

  const getObjFeaturesFromRPFacets = (rspObjFeatures) => {
    let objFeatures = [];
    let facets = {};
    rspObjFeatures.forEach(facet => {
      let feature = (({ refRentalParameterId, refDataType, RentalParameter_Name, DataType_Name }) =>
        ({ refRentalParameterId, refDataType, RentalParameter_Name, DataType_Name }))(facet);
      feature['RentalParameter' + feature.DataType_Name + 'FacetValue'] = facet['RentalParameter' + feature.DataType_Name + 'FacetValue'];
      facets[facet.refRentalParameterId] = {
        dataType: feature.DataType_Name
      }
      if (facet.DataType_Name === 'Enum') {
        let featureOptions = [];
        facet.RentalParameterEnumFacetValue.forEach(item => {
          featureOptions.push({
            value: item.EnumValue,
            label: item.EnumValue_Name,
            id: item.refRentalParameterId
          });
        })
        feature.featureOptions = featureOptions;
      } else if (facet.DataType_Name === 'Boolean') {
        if (facet.RentalParameterBooleanFacetValue.length && facet.RentalParameterBooleanFacetValue[0].TrueCount > 0) {
          let featureOptions = [];
          featureOptions.push({
            value: true,
            label: 'BOOL_YES',
            id: facet.refRentalParameterId
          });
          // featureOptions.push({
          //   value: false,
          //   label: 'BOOL_NO',
          //   id: facet.refRentalParameterId
          // });
          feature.featureOptions = featureOptions;
        }
      }
      if (facet.DataType_Name === 'Boolean') {
        if (feature.featureOptions?.length) {
          objFeatures.push(feature);
        }
      } else {
        objFeatures.push(feature);
      }
    })
    const enums = objFeatures.filter(feature => ['Enum', 'Boolean'].includes(feature.DataType_Name));
    const ranges = objFeatures.filter(feature => !['Enum', 'Boolean'].includes(feature.DataType_Name));
    return { enums, ranges, facets };    
  }

  const getSubcatLocations = (rspOrgCategories) => {
    let subcatLocations = {};

    let _uniqLocations = _.chain(rspOrgCategories).map(e => _.pick(e, [
      'BusinessUnit_Id', 'BusinessUnit_Name', 'BusinessUnit_Latitude', 'BusinessUnit_Longitude'
    ])).uniqWith(_.isEqual).value();

    subcatLocations["All"] = [];
    _uniqLocations.forEach(loc => {
      subcatLocations["All"].push({
        id: loc.BusinessUnit_Id,
        text: loc.BusinessUnit_Name,
        lat: loc.BusinessUnit_Latitude,
        lng: loc.BusinessUnit_Longitude
      });
    })

    let _uniqSubcatIds = _.chain(rspOrgCategories).map('RentalSubcategory_Id').uniq().value();
    _uniqSubcatIds.forEach(subcatId => {
      let filterSubcat = rspOrgCategories.filter(item => item.RentalSubcategory_Id === subcatId);
      subcatLocations[subcatId] = filterSubcat.map(item => ({
        id: item.BusinessUnit_Id,
        text: item.BusinessUnit_Name,
        lat: item.BusinessUnit_Latitude,
        lng: item.BusinessUnit_Longitude
      }));
    })

    return subcatLocations;
  }

  const getLocationCategories = (rspOrgCategories) => {
    let locationCategories = {};

    let _uniqCatIdNames = _.chain(rspOrgCategories).map(e => _.pick(e, ['Name', 'RentalCategory_Id'])).uniqWith(_.isEqual).value();

    locationCategories["All"] = [];
    _uniqCatIdNames.forEach(catIdName => {
      let filterCat = rspOrgCategories.filter(item => item.Name === catIdName.Name);
      let childItems = filterCat.map(item => ({
        id: item.RentalSubcategory_Id,
        text: item.RentalSubcategory_Name
      }));
      locationCategories["All"].push({
        text: catIdName.Name,
        categoryId: catIdName.RentalCategory_Id,
        children: _.uniqWith(childItems, _.isEqual)
      });
    });

    let _uniqLocIds = _.chain(rspOrgCategories).map('BusinessUnit_Id').uniq().value();
    _uniqLocIds.forEach(locId => {
      let filterLoc = rspOrgCategories.filter(item => item.BusinessUnit_Id === locId);
      let _uniqCatIdNamesForLoc = _.chain(filterLoc).map(e => _.pick(e, ['Name', 'RentalCategory_Id'])).uniqWith(_.isEqual).value();
      locationCategories[locId] = [];
      _uniqCatIdNamesForLoc.forEach(catIdName => {
        let filterCat = filterLoc.filter(item => item.Name === catIdName.Name);
        let childItems = filterCat.map(item => ({
          id: item.RentalSubcategory_Id,
          text: item.RentalSubcategory_Name
        }));
        locationCategories[locId].push({
          text: catIdName.Name,
          categoryId: catIdName.RentalCategory_Id,
          children: childItems
        });
      });
    });

    return locationCategories;
  }

  const getOrgSettings = async (orgCode) => {
    try {
      let response = await axios.get(`/clients/prime/release/assets/portals/${orgCode}/settings.json`);
      return response.data;
    }
    catch (e) {
      return {};
    }
  }

  const roundToHour = (date) => {
    const p = 60 * 60 * 1000;
    return new Date(Math.ceil(date.getTime() / p) * p);
  }

  const setDefaultDateTimes = async (params) => {
    let response = await axios.get(`/lessors/ServerTime.ashx`);
    let serverTime = response.data.DateTime;
    let startDate = new Date(serverTime);
    params.startDate = roundToHour(startDate);

    let endDate = new Date(params.startDate.getTime() + 3600000);
    params.endDate = endDate;
  }

  // const setDefaultDateTimes = (params) => {
  //   let startDate = new Date();
  //   //startDate.setDate(startDate.getDate() + 1);
  //   startDate.setHours(9);
  //   startDate.setMinutes(0);
  //   params.startDate = startDate;

  //   let endDate = new Date();
  //   endDate.setDate(endDate.getDate() + 1);
  //   endDate.setHours(17);
  //   endDate.setMinutes(0);
  //   params.endDate = endDate;
  // }

  function clean(obj) {
    for (var propName in obj) {
      if (obj[propName] === null || obj[propName] === undefined) {
        delete obj[propName];
      }
    }
    return obj;
  }
  const searchObjects = async (searchParams, pageNumber, facetFilters) => {
    try {
      let schParams = { ...searchParams };
      schParams = clean(schParams);
      if (!(orderId && isSameTimePeriod)) {
        schParams.organizationId = orgId;
      }
      schParams.pageNumber = pageNumber;
      schParams.pageSize = 9;
      schParams.startDate = dateToISOString(schParams.startDate);
      schParams.endDate = dateToISOString(schParams.endDate, { addDays: 1 });
      // if (orderId) {
      //   if (!orderRentDates.startDate || !orderRentDates.endDate) {
      //     const firstTxn = orderData?.OrderItem[0].RentalTransaction[0];
      //     const rStart = new Date(firstTxn?.RentingStartDate.substring(0, 19));
      //     const rEnd = new Date(firstTxn?.RentingEndDate.substring(0, 19));
      //     schParams.startDate = dateToISOString(rStart);
      //     schParams.endDate = dateToISOString(rEnd);
      //   } else {
      //     schParams.startDate = dateToISOString(new Date(orderRentDates.startDate));
      //     schParams.endDate = dateToISOString(new Date(orderRentDates.endDate));
      //   }
      // } else {
      //   schParams.startDate = dateToISOString(schParams.startDate);
      //   schParams.endDate = dateToISOString(schParams.endDate, { addDays: 1 });
      // }
      let queryString = Object.keys(schParams).map(key => schParams[key] ? key + '=' + schParams[key] : '').join('&');
      /*
  //https://portaldev.wy.by/api/public/SearchObjects
  ?rentalCategoryId=2e820f96-4110-4cb0-999f-b7cce8331098
  &organizationId=1601a7c1-8568-40cc-bfad-d7e972e498c9
  &rentalSubcategoryId=a3d1e42a-aff0-4463-9d69-9c7b7c54a881
  &businessUnitId=375f0d1b-9da0-4100-8e18-322e91422f17
  &StartDate=2020-08-21&EndDate=2020-09-20
  &pageNumber=1
  &pageSize=10
  */
      let searchUrl = '';
      if (orderId && isSameTimePeriod) {
        searchUrl = `${apiBasePublic}Order/${orderId}/v2/SearchObjects?${queryString}`;
      }
      else {
        searchUrl = `${apiBasePublic}SearchObjectsWithFeatures?${queryString}`;
      }
      if (facetFilters && facetFilters.length) {
        const postData = {
          ROOT: {
            RentalParameterFacetFilter: facetFilters
          }
        }
        const result = await axios.post(searchUrl, postData);
        return result.data.ROOT;
      } else {
        const result = await axios(searchUrl);
        return result.data.ROOT;
      }
    }
    catch (error) {
      handleError(error);
    }
  }

  const createEnumFilter = (key, values) => {
    let result = {
      refRentalParameterId: key,
      RentalParameterEnumFacetFilter: []
    }
    values.forEach((item) => {
      result.RentalParameterEnumFacetFilter.push({ EnumValue: item.value });
    })
    return result;
  }

  const createBooleanFilter = (key, values) => {
    let result = {
      refRentalParameterId: key,
      RentalParameterBooleanFacetFilter: [
        {
          "FilterFalse": values.some(item => (item.value === false)) ? 1 : 0,
          "FilterTrue": values.some(item => (item.value === true)) ? 1 : 0
        }
      ]
    }
    return result;
  }

  const createIntegerFilter = (key, value) => {
    let result = {
      refRentalParameterId: key,
      RentalParameterIntegerFacetFilter: [
        {
          Min: value.min,
          Max: value.max
        }
      ]
    }
    return result;
  }

  const createFloatFilter = (key, value) => {
    let result = {
      refRentalParameterId: key,
      RentalParameterFloatFacetFilter: [
        {
          Min: value.min,
          Max: value.max
        }
      ]
    }
    return result;
  }

  const searchObjectsWithFilters = async (searchParams, pageNumber, { optionsSelected, rangeFilters }) => {
    try {
      let facetFilters = [];
      for (const [key, value] of Object.entries(optionsSelected)) {
        //console.log(`${key}: ${value}`);
        let facet = searchPageVars.objFacets[key];
        //console.log(facet);
        if (facet.dataType === 'Enum') {
          const filter = createEnumFilter(key, value);
          facetFilters.push(filter);
        } else if (facet.dataType === 'Boolean') {
          const filter = createBooleanFilter(key, value);
          facetFilters.push(filter);
        }
      }
      for (const [key, value] of Object.entries(rangeFilters)) {
        //console.log(`${key}: ${value}`);
        let facet = searchPageVars.objFacets[key];
        // console.log(facet);
        if (facet.dataType === 'Integer') {
          const filter = createIntegerFilter(key, value);
          facetFilters.push(filter);
        } else if (facet.dataType === 'Float') {
          const filter = createFloatFilter(key, value);
          facetFilters.push(filter);
        }
      }
      return await searchObjects(searchParams, pageNumber, facetFilters);
    }
    catch (error) {
      handleError(error);
    }
  }

  const fetchObjectDetailsMin = async (objectId) => {
    try {
      const objectUrl = `${apiBasePublic}RentalObject/${objectId}/json?organizationId=${orgId}`;
      const objectResult = await axios(objectUrl);
      return objectResult.data.ROOT.RentalObject[0];
    }
    catch (error) {
      handleError(error);
    }
  }

  const fetchObjectDetails = async (params) => {
    try {
      setObjectDetails(null);
      let objectId = params.objectId;
      const startDate = dateToISOString(params.startDate);
      let endDate = dateToISOString(params.endDate, { addDays: 1 });
      // let showTime = masterData.organization.showTime;
      // if (!showTime) {
      //   let eDate = new Date(params.endDate.getTime());
      //   eDate.setDate(eDate.getDate() + 1);
      //   endDate = dateToISOString(eDate);
      // }
      const objectUrl = `${apiBasePublic}RentalObject/${objectId}/json?organizationId=${orgId}`;
      const objectResult = await axios(objectUrl);
      const rentalObject = objectResult.data.ROOT.RentalObject[0];
      const businessUnitId = rentalObject.refBusinessUnitId;
      const businessUnitUrl = `${apiBasePublic}BusinessUnit/${businessUnitId}/json?organizationId=${orgId}`;
      const businessUnitResult = await axios(businessUnitUrl);
      const businessUnit = businessUnitResult.data.ROOT.BusinessUnit[0];
      let optionsUrl = `${apiBasePublic}RentalObject/${objectId}/Options?startDate=${startDate}&endDate=${endDate}`;
      if (params.onlyExactMatch) {
        optionsUrl += `&onlyExactMatch=true`;
      }
      const optionsResult = await axios(optionsUrl);
      const objectOptions = optionsResult.data.ROOT.RentalObject[0];
      const objectExceptions = optionsResult.data.ROOT.RuleExceptionData;
      await updateOfferDates(objectOptions);
      setCurrentObjId(objectId);
      setObjectDetails({
        rentalObject,
        businessUnit,
        objectOptions,
        objectExceptions
      });
      setLSObjectDetailsMap({
        ...LSObjectDetailsMap, [rentalObject.Id]: { rentalObject, businessUnit, objectOptions, objectExceptions }
      });
      return { rentalObject, businessUnit, objectOptions, objectExceptions };
    }
    catch (error) {
      handleError(error);
    }
  }

  const fetchObjectOptions = async (params) => {
    try {
      let objectId = params.objectId;
      const startDate = dateToISOString(params.startDate);
      const daysToAdd = masterData.organization.refAllotmentTimeUnitId === TIMEUNIT_ID.NIGHT ? 0 : 1;
      let endDate = dateToISOString(params.endDate, { addDays: daysToAdd });
      let optionsUrl = `${apiBasePublic}RentalObject/${objectId}/Options?startDate=${startDate}&endDate=${endDate}`;
      if (params.onlyExactMatch) {
        optionsUrl += `&onlyExactMatch=true`;
      }
      const optionsResult = await axios(optionsUrl);
      const objectOptions = optionsResult.data.ROOT.RentalObject[0];
      const objectExceptions = optionsResult.data.ROOT.RuleExceptionData;
      await updateOfferDates(objectOptions);

      return { objectOptions, objectExceptions };
    }
    catch (error) {
      handleError(error);
    }
  }

  const updateOfferDates = async (objectOptions) => {
    if (objectOptions && objectOptions.RentalObjectOffer && objectOptions.RentalObjectOffer.length > 0) {
      const objectId = objectOptions.Id;
      const offer = objectOptions.RentalObjectOffer[0];
      if (offer.RentalTransactionOfferSelectable && offer.RentalTransactionOfferSelectable.length > 0) {
        const offerSel = offer.RentalTransactionOfferSelectable[0];
        var newParams = {};
        newParams.startDate = new Date(offerSel.StartDate.substring(0, 19));
        let eDate = new Date(offerSel.EndDate.substring(0, 19));
        if (masterData.organization.refAllotmentTimeUnitId === TIMEUNIT_ID.NIGHT) {
          //eDate.setDate(eDate.getDate() + 1);
        }
        newParams.endDate = eDate;
        setOfferDates({ ...newParams });

        if (masterData.organization.showTime) {
          setSearchParams({ ...searchParams, ...newParams });
        } else {
          const startStr = offerSel.StartDate.substr(0, 19);
          let endStr = offerSel.EndDate.substr(0, 19);
          if (masterData.organization.refAllotmentTimeUnitId === TIMEUNIT_ID.NIGHT) {
            endStr = dateToISOString(new Date(endStr), { addDays: 1 });
          }
          let contentUPDT = await getUnavailablePickupAndDropoffTimes(objectId, startStr, endStr);
          let pickupDropoff = getPickupDropoff(contentUPDT, newParams.startDate, newParams.endDate);
          if (pickupDropoff.pickup) {
            newParams.startDate = new Date(pickupDropoff.pickup);
          }
          if (pickupDropoff.dropoff) {
            let seDate = new Date(pickupDropoff.dropoff);
            if (masterData.organization.refAllotmentTimeUnitId === TIMEUNIT_ID.NIGHT) {
              seDate.setDate(seDate.getDate() + 1);
            }            
            newParams.endDate = seDate;
          }
          setSearchParams({ ...searchParams, ...newParams });
        }
      }
    }
  }

  const getPickupDropoff = (contentUPDT, startDT, endDT) => {
    const rentStart = dateToISOString(startDT, { ignoreTime: true });
    const rentEnd = dateToISOString(endDT, { ignoreTime: true });
    let result = {};
    const updtStart = contentUPDT.find(item => item.Date.substr(0, 10) === rentStart);
    if (updtStart) {
      if (updtStart.UnavailablePickupAndDropoffInterval.length) {
        result.pickup = updtStart.UnavailablePickupAndDropoffInterval[0].DateTo.substring(0, 19);
      }
    }
    //reduce a day
    let rentEnd1 = new Date(endDT.getTime());
    rentEnd1.setDate(rentEnd1.getDate() - 1);
    const rentEndB4 = dateToISOString(rentEnd1, { ignoreTime: true });
    const updtEnd = contentUPDT.find(item => item.Date.substr(0, 10) === rentEndB4);
    if (updtEnd) {
      let lngth = updtEnd.UnavailablePickupAndDropoffInterval.length;
      if (lngth) {
        result.dropoff = updtEnd.UnavailablePickupAndDropoffInterval[lngth - 1].DateFrom.substring(0, 19);
      }
    }
    return result;
  }

  const getTimelineAllotmentWithSeasons = async (objectId, startDate, endDate) => {
    try {
      const tawsUrl = `${apiBasePublic}RentalObject/getTimelineAllotmentWithSeasons?organizationId=${orgId}&ids=${objectId}&startDate=${startDate}&endDate=${endDate}`;
      const tawsResult = await axios(tawsUrl);
      const taws = tawsResult.data.ROOT.RentalObject[0].RentalObjectBookableAllotment;
      return taws;
    }
    catch (error) {
      handleError(error);
    }
  }

  const getUnavailablePickupAndDropoffTimes = async (objectId, startDate, endDate) => {
    try {
      const updtUrl = `${apiBasePublic}RentalObject/${objectId}/getUnavailablePickupAndDropoffTimes?organizationId=${orgId}&startDate=${startDate}&endDate=${endDate}`;
      const updtResult = await axios(updtUrl);
      const updt = (updtResult.data.ROOT.RentalObject[0] || {}).Day;
      return updt;
    }
    catch (error) {
      handleError(error);
    }
  }

  const fetchCalendarDetails = async (params) => {
    try {
      let objectId = params.objectId;
      const startDate = dateToISOString(params.startDate);
      const endDate = dateToISOString(params.calEnd);
      const tawsUrl = `${apiBasePublic}RentalObject/getTimelineAllotmentWithSeasons?organizationId=${orgId}&ids=${objectId}&startDate=${startDate}&endDate=${endDate}`;
      const tawsResult = await axios(tawsUrl);
      const taws = tawsResult.data.ROOT.RentalObject[0].RentalObjectBookableAllotment;

      const updtUrl = `${apiBasePublic}RentalObject/${objectId}/getUnavailablePickupAndDropoffTimes?organizationId=${orgId}&startDate=${startDate}&endDate=${endDate}`;
      const updtResult = await axios(updtUrl);
      const updt = (updtResult.data.ROOT.RentalObject[0] || {}).Day;

      //return { taws, updt };
    }
    catch (error) {
      handleError(error);
    }
  }

  const createTxn = async (objectId, exactMatch) => {
    try {
      const startDate = dateToISOString(offerDates.startDate);
      const endDate = dateToISOString(offerDates.endDate);
      let postData = {
        TransactionRequest: {
          refOrganizationId: orgId,
          refRentalObjectId: objectId,
          RentingStartDate: startDate,
          RentingEndDate: endDate,
          Quantity: 1,
          OnlyExactMatch: exactMatch
        }
      }
      const url = `${apiBasePublic}RentalTransaction/Create`;
      const txnResult = await axios.post(url, postData);
      const txn = txnResult.data.ROOT.RentalTransaction[0];
      setOrderRentDates({
        startDate: txn.PickupStartDate.substring(0, 19),
        endDate: txn.DropoffEndDate.substring(0, 19),
      });
      setSearchParams({
        ...searchParams,
        startDate: new Date(roundMinutes(txn.PickupStartDate.substring(0, 19))),
        endDate: new Date(roundMinutes(txn.DropoffEndDate.substring(0, 19)))
      });
      setCurrentTxnId(txn?.Id);
      return txn ? txn.Id : false;
    }
    catch (error) {
      handleError(error);
      console.log(error.response.data);
    }
  }

  const fetchTransaction = async (txnId) => {
    try {
      const url = `${apiBasePublic}RentalTransaction/${txnId}`;
      const getResult = await axios.get(url);
      const txn = getResult.data.ROOT?.RentalTransaction[0];
      if (!txn) {
        throw getResult.data.Message;
      } else {
        return txn;
      }
    }
    catch (error) {
      handleError(error);
    }
  }

  const addService = async (id, serviceId, accessoryId) => {
    try {
      // let postData = {
      //   rentalServiceId: serviceId
      // };
      // if(accessoryId) {
      //   postData.rentalTransactionAccessoryId = accessoryId;
      // }
      const url = `${apiBasePublic}RentalTransaction/${id}/AddService?rentalServiceId=${serviceId}`;
      if (accessoryId) {
        url += `&rentalTransactionAccessoryId=${accessoryId}`;
      }
      const svcResult = await axios.post(url);
      const txn = svcResult.data.ROOT?.RentalTransaction[0];
      if (!txn) {
        throw svcResult.data.Message;
      } else {
        //setCurrentTxn(txn);
        return txn;
      }
    }
    catch (error) {
      handleError(error);
    }
  }

  const removeService = async (id, serviceId) => {
    try {
      // let postData = {
      //   rentalServiceId: serviceId
      // };
      const url = `${apiBasePublic}RentalTransaction/${id}/RemoveService?rentalServiceId=${serviceId}`;
      const svcResult = await axios.post(url);
      const txn = svcResult.data.ROOT?.RentalTransaction[0];
      if (!txn) {
        throw svcResult.data.Message;
      } else {
        //setCurrentTxn(txn);
        return txn;
      }
    }
    catch (error) {
      handleError(error);
    }
  }

  const setAccessory = async (id, accessoryId, targetQuantity) => {
    try {
      let url = `${apiBasePublic}RentalTransaction/${id}/SetAccessory?accessoryId=${accessoryId}`;
      if (targetQuantity && targetQuantity > 1) {
        url += `&targetQuantity=${targetQuantity}`;
      }
      const accResult = await axios.post(url);
      const txnArr = accResult.data.ROOT?.RentalTransaction;
      if (txnArr && txnArr.length) {
        return txnArr[0];
      } else if (accResult.data.ROOT.RuleExceptionData && accResult.data.ROOT.RuleExceptionData.length) {
        handleError(accResult.data.ROOT.RuleExceptionData[0].Message || "Error");
      } else {
        handleError(accResult);
      }
    }
    catch (error) {
      handleError(error);
    }
  }

  const updateAccessory = async (id, accessoryId, targetQuantity) => {
    try {
      // let postData = {
      //   accessoryId, targetQuantity
      // };
      const url = `${apiBasePublic}RentalTransaction/${id}/UpdateAccessory?accessoryId=${accessoryId}&targetQuantity=${targetQuantity}`;
      const accResult = await axios.post(url);
      const txn = accResult.data.ROOT?.RentalTransaction[0];
      if (!txn) {
        throw accResult.data.Message;
      } else {
        //setCurrentTxn(txn);
        return txn;
      }
    }
    catch (error) {
      handleError(error);
    }
  }

  const updateTransaction = async (id, note, note2, mainObjectQuantity) => {
    try {
      const url = `${apiBasePublic}RentalTransaction/${id}/Update`;
      const formData = { note, note2 };
      if (mainObjectQuantity > 0) {
        formData.mainObjectQuantity = mainObjectQuantity;
      }
      const options = {
        method: 'POST',
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        data: queryString.stringify(formData),
        url
      };
      const result = await axios(options);
      const txn = result.data.ROOT?.RentalTransaction[0];
      if (!txn) {
        console.log('txn not updated...');
        throw result.data.Message;
      } else {
        console.log('updated txn succ');
        //setCurrentTxn(txn);
        return txn;
      }
    }
    catch (error) {
      console.log('update txn error');
      handleError(error);
      return false;
    }
  }

  const ajaxLogin = async (username, password) => {
    var postData = {
      username, password
    }
    const url = `/AjaxLogin`;
    const loginResult = await axios.post(url, postData);
    return loginResult.data;
  }

  const getAuthUser = async () => {
    const url = `${apiBasePublic}GetAuthenticatedUser?organizationId=${orgId}`;
    const getResult = await axios.get(url);
    return getResult.data.ROOT.OrganizationBusinessPartner[0];
  }

  const ajaxSignout = async () => {
    const url = `/AjaxSignout`;
    const signoutResult = await axios.get(url);
    return signoutResult.data;
  }

  const fetchPersonalData = async (lesseeId) => {
    let url = `${apiBase}PersonalData?organizationId=${orgId}`;
    if (lesseeId) {
      url += `&lesseeId=${lesseeId}`;
    }
    const getResult = await axios.get(url);
    return getResult.data.ROOT;
  }

  const createProfile = async (businessUnit, businessPartner, addressPost, addressBill) => {
    const profileOrgId = genGuid();
    const profileBUId = genGuid();
    const profileOBPId = genGuid();
    const profileUBPId = genGuid();
    const profilePAId = genGuid();
    const profileBAId = genGuid();

    const profilePostAddress = { Id: profilePAId, ...addressPost };
    const profileBillAddress = { Id: profileBAId, ...addressBill };

    let profileData = {
      Organization: {
        Id: profileOrgId,
        BusinessUnit: {
          Id: profileBUId,
          refTimeZoneId: businessUnit.refTimeZoneId,
          BusinessUnitAddress: [profilePostAddress]
        },
        OrganizationBusinessPartner: {
          "Id": profileOBPId,
          "refBusinessUnitId": profileBUId,
          "refOrganizationId": profileOrgId,
          "refUserBusinessPartnerId": profileUBPId,
          ...businessPartner
        }
      }
    }

    if (addressBill) {
      profileData.Organization.BusinessUnit.BusinessUnitAddress.push(profileBillAddress);
    }

    try {
      let url = `${apiBasePublic}CreateNewLessee?lessorOrganizationId=${orgId}`;
      await axios.post(url, profileData);
      return true;
    }
    catch (error) {
      throw error;
    }
  }

  const confirmUserEmail = async (userId, token) => {
    try {
      const url = `/confirmUserEmail.json?userId=${userId}&token=${token}`;
      const result = await axios.get(url);
      return true;
    }
    catch (error) {
      handleError(error);
      return false;
    }
  }

  const invokeResetPassword = async (email) => {
    try {
      const url = `${apiBasePublic}InvokeResetPassword`;
      let postData = {
        email,
        organizationId: orgId
      }
      const result = await axios.post(url, postData);
      return true;
    }
    catch (error) {
      handleError(error);
      return false;
    }
  }

  const resetPassword = async (postData) => {
    try {
      const url = `${apiBasePublic}ResetPassword`;
      const result = await axios.post(url, postData);
      return true;
    }
    catch (error) {
      handleError(error);
      return false;
    }
  }

  const updateProfile = async (profileData, lesseeId) => {
    try {
      const url = `${apiBase}PersonalData?organizationId=${orgId}&lesseeId=${lesseeId}`;
      await axios.put(url, profileData);
      return true;
    }
    catch (error) {
      throw error;
    }
  }

  const changePassword = async (profileData) => {
    try {
      const url = `${apiBase}Lessee/ChangePassword`;
      await axios.post(url, profileData);
      return true;
    }
    catch (error) {
      throw error;
    }
  }

  const createOrUpdateOrder = async (ordrId, txnId) => {
    try {
      let isGuestBooking = false;
      let url = `${apiBasePublic}Order/AddItem?rentalTransactionId=${txnId}`;
      if (ordrId) {
        url += `&orderId=${ordrId}`;
        // const currentUserUrl = `${apiBasePublic}RentalTransaction/${txnId}/SetCurrentUserAsALessee`
        // await axios.get(currentUserUrl);
      }

      const orderResult = await axios.post(url);
      const order = orderResult.data.ROOT.Order[0];
      updateOrderDetails(order);

      var lsigb = localStorage.getItem("ls_igb");
      if (lsigb) {
        if (lsigb !== 'undefined') {
          isGuestBooking = JSON.parse(lsigb);
        }
      }
      console.log("isGuestBooking:" + isGuestBooking);
      if (!ordrId && !isGuestBooking) {
        console.log('setting lessee data for order: ' + order.Id);
        await setLesseeData(order.Id);
      }

      return ordrId ? true : order.Id;
    }
    catch (error) {
      //handleError(error);
      //handleError(error.response.data);
      console.log(error);
      return false;
    }
  }

  const removeOrderItem = async (orderId, txnId) => {
    try {
      let url = `${apiBasePublic}Order/${orderId}/RemoveItem?rentalTransactionId=${txnId}`;
      const orderResult = await axios.post(url);
      const order = orderResult.data.ROOT.Order[0];
      if (order && order.OrderItem && !order.OrderItem.length) {
        await cancelOrder(order.Id);
        return 'ORDER_CANCELLED';
      } else {
        updateOrderDetails(order);
      }
      return true;
    }
    catch (error) {
      console.log(error);
      handleError(error.response.data);
    }
  }

  const isValidOrder = (order) => {
    let txns = order.OrderItem;
    if (txns) {
      let first = txns[0].RentalTransaction[0];
      console.log('isValid:' + (first.refRentalTransactionStatusId !== BOOKING_STATUS_TYPE.INTERRUPTED));
      return first.refRentalTransactionStatusId !== BOOKING_STATUS_TYPE.INTERRUPTED;
    }
    return false;
  }

  const getOrderDetails = async (orderId) => {
    try {
      let url = `${apiBasePublic}Order/${orderId}/Detail`;
      const orderResult = await axios.get(url);
      const order = orderResult.data.ROOT.Order[0];

      if (!isValidOrder(order)) {
        console.log('invalid order. canceling...');
        clearOrder();
        //cancelOrder(order.Id);
        return;
      }

      updateOrderDetails(order);
      console.log('loaded order data');
      if (order && order.OrderItem && order.OrderItem.length) {
        return true;
      }
      else {
        return false;
      }
    }
    catch (error) {
      let errData = error.response?.data;
      if (errData) {
        console.log(error.response.data);
        handleError(error.response.data);
      } else {
        console.log(error);
        handleError(error);
      }
    }
  }

  const cancelOrder = async (orderId) => {
    try {
      let url = `${apiBasePublic}Order/${orderId}/Delete`;
      const orderResult = await axios.post(url);
      const order = orderResult.data.ROOT.Order[0];
      updateOrderDetails(order);
      return true;
    }
    catch (error) {
      console.log(error.response.data);
      handleError(error.response.data);
    }
  }

  const refreshOrder = async (orderId) => {
    try {
      let url = `${apiBasePublic}Order/${orderId}/Refresh`;
      const orderResult = await axios.post(url);
      const orderNew = orderResult.data.ROOT.Order[0];
      if (orderNew) {
        orderNew.OrderItem = orderData.OrderItem;
        setOrderData({ ...orderNew });
        return true;
      }
      return false;
    }
    catch (error) {
      console.log(error.response.data);
      handleError(error.response.data);
    }
  }

  const checkVoucherCode = async (orderId, businessUnitId, code) => {
    try {
      let url = `${apiBasePublic}Order/${orderId}/CheckVoucher?code=${code}&businessUnitId=${businessUnitId}`;
      const svcResult = await axios.get(url);
      console.log('voucher valid:' + svcResult.status);
      const orderNew = svcResult.data.ROOT.Order[0];
      if (orderNew) {
        orderNew.OrderItem.forEach(item => {
          item.key = genGuid();
          item.RentalTransaction[0].key = genGuid();
        })
        setOrderData(orderNew);
        return true;
      }
    }
    catch (error) {
      if (error.response && error.response.status) {
        console.log('checkVoucherCode failed.' + error.response.status);
        var statusCode = error.response.status;
        if (statusCode >= 400 && statusCode < 500) {
          let url = `${apiBasePublic}Order/${orderId}/Detail`;
          const orderResult = await axios.get(url);
          const orderNew = orderResult.data.ROOT.Order[0];
          if (orderNew) {
            orderNew.OrderItem.forEach(item => {
              item.key = genGuid();
              item.RentalTransaction[0].key = genGuid();
            })
            setOrderData(orderNew);
          }
        }
      }
      handleError(error);
    }
  }

  const bookOrder = async (orderId, paymentMethodId, dataTransReturnLocation, orderTotal) => {
    try {
      let url = `${apiBasePublic}Order/${orderId}/Book?paymentMethodId=${paymentMethodId}`;
      if (dataTransReturnLocation) {
        url += `&dataTransReturnLocation=${dataTransReturnLocation}`;
      }
      const orderResult = await axios.post(url);
      if (!orderTotal || !dataTransReturnLocation) {
        const order = orderResult.data.ROOT.Order[0];
        updateOrderDetails(order, true);
        return true;
      } else {
        const dtp = orderResult.data.ROOT.DataTransPayment[0];
        return dtp;
      }
    }
    catch (error) {
      handleError(error);
      //console.log(error.response.data);
    }
  }

  const processCCGWRequest = (result) => {
    const url = `${masterData.organization.DataTransUPPUrl}${result.UPPTransactionId}`;
    document.location.assign(url);
  }

  const setLesseeData = async (ordrId) => {
    try {
      let url = `${apiBasePublic}Order/${ordrId}/SetLesseeData`;
      const orderResult = await axios.post(url);
      // const order = orderResult.data.ROOT.Order[0];
      // updateOrderDetails(order);
      return true;
    }
    catch (error) {
      handleError(error);
      console.log(error.response.data);
      return false;
    }
  }

  const updatePersonalData = async (ordrId, cobpAddressesArr, currentProfile, lesseeId) => {
    try {
      const postData = {
        ROOT: {
          Order: [
            {
              Id: ordrId,
              CustomerAddress: cobpAddressesArr
            },
          ],
        },
      };
      let url = `${apiBasePublic}Order/${ordrId}/EditCustomerDetails`;
      if (lesseeId) {
        url += `?lesseeId=${lesseeId}`;
      }
      await axios.post(url, postData);
      await updateProfile(currentProfile, lesseeId);
      return true;
    }
    catch (error) {
      console.log(error);
      handleError(error);
    }
  }

  const storeGuestData = (businessPartner, addressPost, addressBill) => {
    setLSGuestData({ businessPartner, addressPost, addressBill });
    return true;
  }

  const setGuestData = async (ordrId) => {
    try {
      let { businessPartner, addressPost, addressBill } = LSGuestData;
      const idAddressPost = genGuid();
      const idAddressBill = genGuid();
      const newOrgId = genGuid();
      const newBUId = genGuid();
      const idOBP = genGuid();
      const postData = {
        ROOT: {
          Organization: {
            Id: newOrgId,
            BusinessUnit: {
              Id: newBUId,
              BusinessUnitAddress: [
                { ...addressPost, Id: idAddressPost },
                { ...addressBill, Id: idAddressBill }
              ]
            },
            OrganizationBusinessPartner: {
              ...businessPartner,
              Id: idOBP,
              refBusinessUnitId: newBUId,
              refOrganizationId: newOrgId
            }
          }
        }
      }
      let url = `${apiBasePublic}Order/${ordrId}/SetGuestData`;
      await axios.post(url, postData);
      return true;
    }
    catch (error) {
      handleError(error);
      return false;
    }
  }

  const updateOrderDetails = (order, isConfirmed) => {
    if (order) {
      setLSOrderId(order.Id);
      setOrderId(order.Id);
      setOrderData(order);
      if (isConfirmed) {
        setLSObjectDetailsMap(null);
        setCartCount(0);
        setSearchParams({ ...searchParams, ...defaultParams });
      }
      else if (order.OrderItem && order.OrderItem.length) {
        setCartCount(order.OrderItem.length);
        //updateSearchParamsFromOrder(order);
      }
    }
    else {
      setLSOrderId('');
      setOrderId('');
      //setOrderData(null);
      setLSObjectDetailsMap(null);
      setCartCount(0);
      setSearchParams({ ...searchParams, ...defaultParams });
    }
  }

  const clearOrder = () => {
    setOrderId('');
    setOrderData(null);
    setCartCount(0);
    setIsDBA(false);
    setIsAgbAccepted(false);

    localStorage.removeItem("ls_gd");
    localStorage.removeItem("ls_igb");
    localStorage.removeItem("ls_odm");
    localStorage.removeItem("ls_oid");
  }


  const updateSearchParamsFromOrder = (order) => {
    let rtArr = order.OrderItem[0]?.RentalTransaction;
    if (!rtArr || !rtArr.length) return;
    let txn = rtArr[0];
    if (!txn) return;
    let newParams = {};
    newParams.startDate = roundMinutes(txn.PickupStartDate.substring(0, 19));
    newParams.endDate = roundMinutes(txn.DropoffEndDate.substring(0, 19));
    setSearchParams({ ...searchParams, ...newParams });
  }

  const fetchRecentBookings = async ({ startDT, endDT, pageSize, pageNumber, isActive }) => {
    try {
      let url = `${apiBase}Lessee/RentalTransaction/ListMy${isActive ? 'Active' : ''}?lessorOrganizationId=` + orgId;
      const startDate = dateToISOString(startDT, { ignoreTime: true });
      const endDate = dateToISOString(endDT, { ignoreTime: true });
      let params = { startDate, endDate, pageSize, pageNumber };
      const getResult = await axios.get(url, { params });
      return getResult.data.ROOT.RentalTransaction;
    }
    catch (error) {
      handleError(error);
    }
  }

  const fetchBookingDetails = async (bookingId) => {
    try {
      let url = `${apiBase}Lessee/RentalTransaction/${bookingId}`;
      const getResult = await axios.get(url);
      return getResult.data.ROOT.RentalTransaction[0];
    }
    catch (error) {
      handleError(error);
    }
  }

  const getCancellationFee = async (bookingId) => {
    try {
      let url = `${apiBase}Lessee/RentalTransaction/${bookingId}/computeCancellationFee`;
      const getResult = await axios.get(url);
      return getResult.data.ROOT;
    }
    catch (error) {
      handleError(error);
    }
  }

  const cancelBooking = async (bookingId, ffStatusCancelId) => {
    try {
      let postData = {
        ROOT: {
          RentalTransaction: [
            {
              Id: bookingId,
              refRentalTransactionFulfillmentStatusId: ffStatusCancelId
            }
          ]
        }
      }
      let url = `${apiBase}Lessee/RentalTransaction/${bookingId}/Edit`;
      const cancelResult = await axios.post(url, postData);
      return cancelResult.data.ROOT.RentalTransaction[0];
    }
    catch (error) {
      handleError(error);
    }
  }

  const cancelTemporaryBooking = async (isTempTxn) => {
    if (cartCount > 0 && !isTempTxn) {
      await removeOrderItem(orderId, currentTxnId);
      return true;
    } else {
      await axios.post(`${apiBasePublic}RentalTransaction/${currentTxnId}/interrupt`);
      return true;
    }
  }

  const removeCredentials = async (customerId) => {
    try{
      await axios.get(`${apiBase}Lessee/CustomerOrganizationBusinessPartner/RemoveCredentials?customerId=${customerId}`);
      return true;
    }
    catch (error) {
      handleError(error);
    }    
  }

  const handleError = (error) => {
    let errorMessage = isString(error) ? error : error.response.data?.Message;
    if (!errorMessage) {
      errorMessage = "An error occured!";
    }
    setModalProps({ ...modalProps, showModal: true, messages: [errorMessage] });
  }

  return {
    roundToHour,
    dateToISOString,
    setDefaultDateTimes,
    fetchMasterData,
    getObjFeatures,
    getObjFeaturesFromRPFacets,
    searchObjects,
    searchObjectsWithFilters,
    fetchObjectDetails,
    fetchObjectOptions,
    fetchCalendarDetails,
    getTimelineAllotmentWithSeasons,
    getUnavailablePickupAndDropoffTimes,
    createTxn,
    fetchTransaction,
    createOrUpdateOrder,
    setLesseeData,
    updatePersonalData,
    setGuestData,
    removeOrderItem,
    getOrderDetails,
    cancelOrder,
    refreshOrder,
    clearOrder,
    checkVoucherCode,
    bookOrder,
    processCCGWRequest,
    addService,
    removeService,
    setAccessory,
    updateAccessory,
    updateTransaction,
    fetchRecentBookings,
    fetchBookingDetails,
    getCancellationFee,
    cancelBooking,
    cancelTemporaryBooking,
    fetchObjectDetailsMin,
    ajaxLogin,
    getAuthUser,
    ajaxSignout,
    fetchPersonalData,
    createProfile,
    confirmUserEmail,
    invokeResetPassword,
    resetPassword,
    updateProfile,
    changePassword,
    storeGuestData,
    removeCredentials
  }

}
